/**
 * InlineDocumentViewer - one document rendered inline, as a self-contained pane.
 *
 * The Ablage viewer (`DocumentViewerScreen`) owns a whole route; this is the same
 * preview as a component, so the Bearbeitungsmaske can put it beside the mail. Both
 * share one blob-detour implementation underneath.
 *
 * View, never download: the bytes come from `document.read`'s `/content` route, and
 * this pane offers no download control - `document.download` is a separate permission
 * on a separate route, and the Ablage grid owns that action.
 *
 * Only PDFs render, behind two gates, because an object URL inherits our origin and
 * whatever an iframe renders runs as a same-origin page. Gate 1 refuses anything the
 * server did not declare `application/pdf` (a filename is attacker-supplied); gate 2,
 * in `usePdfObjectUrl`, refuses bytes whose real Content-Type disagrees.
 *
 * The failure state covers a missing document AND one the mail-team gate hides - the
 * api refuses both as the same 404 on purpose - so it must not claim to know which.
 *
 * [Source: apps/web/src/features/documents/DocumentViewerScreen.tsx] (the pane it mirrors)
 */
import type { ReactNode } from 'react';
import { FileText, Maximize2, X } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { fileSize } from '../../../lib/format';
import { usePdfObjectUrl } from '../hooks/usePdfObjectUrl';

interface InlineDocumentViewerProps {
  /** Document id - the `AttachmentView.id` of the file to preview. */
  documentId: string;
  filename: string;
  /** MIME as the SERVER declared it. Absent means no preview - never guess from the name. */
  mimeType?: string | null;
  /** Mock-mode src. Live mode ignores it and fetches the real bytes. */
  mockUrl?: string | null;
  /** Renders a close control when provided. */
  onClose?: () => void;
  /** When provided, renders an "expand" control (S2) — the Bearbeitungsmaske uses it to
   *  open the same document big and centered (Gmail-style), while the inline pane stays
   *  the permanent Teil-1 context. Absent (e.g. the Ablage viewer) → no expand control. */
  onExpand?: () => void;
  /** Shown in the header meta line when known. */
  sizeBytes?: number | null;
  className?: string;
}

/**
 * True when an iframe may render this file: PDF, and only on the SERVER-declared type.
 * A file called `rechnung.pdf` that the server stored as `text/html` must never be
 * trusted on its name, so no declared type means no preview rather than a guess.
 */
export function isInlinePreviewable(mimeType?: string | null): boolean {
  return mimeType === 'application/pdf';
}

function Message({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

export function InlineDocumentViewer({
  documentId,
  filename,
  mimeType = null,
  mockUrl = null,
  onClose,
  onExpand,
  sizeBytes = null,
  className,
}: InlineDocumentViewerProps) {
  const previewable = isInlinePreviewable(mimeType);
  // Gate 1 skips the fetch entirely for anything unrenderable (a null id fetches
  // nothing). `isError` is deliberately unread: a failed fetch and "nothing to show"
  // render the same message, since a hidden document comes back as an ordinary 404.
  const { src, isLoading } = usePdfObjectUrl(previewable ? documentId : null, mockUrl);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg',
        className,
      )}
    >
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
        <div className="grid size-8 shrink-0 place-items-center rounded-md bg-muted">
          <FileText className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground" title={filename}>
            {filename}
          </p>
          <p className="text-xs text-muted-foreground">
            PDF{sizeBytes ? ` · ${fileSize(sizeBytes)}` : ''}
          </p>
        </div>
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            aria-label="Vorschau vergrössern"
            title="Vergrössern"
            className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Maximize2 className="size-4" />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Vorschau schliessen"
            title="Vorschau schliessen (Esc)"
            className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* The PDF's own page ground - a muted band so the white page reads as a sheet
          sitting ON the surface, not as the surface itself. */}
      <div className="min-h-0 flex-1 bg-muted/40">
        {!previewable ? (
          <Message>Für diesen Dateityp gibt es keine Vorschau.</Message>
        ) : src ? (
          <iframe title={`PDF-Vorschau: ${filename}`} src={src} className="size-full border-0" />
        ) : isLoading ? (
          <Message>Lade Vorschau …</Message>
        ) : (
          <Message>Das Dokument ist derzeit nicht verfügbar.</Message>
        )}
      </div>
    </div>
  );
}
